import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { errorText } from "../../api/client";
import { useStore } from "../store";
import { isProductionHost } from "../../shared/origin";
import { KID_CODE_LENGTH, normaliseKidCode } from "../../shared/kid-code";
import { PointzLogo } from "../components/PointzLogo";
import { INTRO_POSTER, IntroPlayer } from "./IntroPlayer";
import { markIntroSeen, shouldShowIntro } from "../intro";
import { forgetInvite, invitedEmail } from "../invite";

// Preview workers and local dev send no email, so they offer a one-tap sign-in instead of
// making reviewers dig the code out of `wrangler tail`. Never shown in production.
const showPreview = import.meta.env.DEV || !isProductionHost(window.location.hostname);

type Who = "adult" | "kid";

const KID_FAILED = "That code didn't work. Ask a grown-up to check it.";


/** Sign-in for both roles: grown-ups by emailed code, kids by the code a parent minted. */
export function Login() {
  const [who, setWho] = useState<Who>("adult");
  const [intro, setIntro] = useState(false);
  // Watched to the end here, it will not open again by itself after sign-in; skipped, it still will.
  const closeIntro = useCallback((completed: boolean) => {
    if (completed) markIntroSeen();
    setIntro(false);
  }, []);

  return (
    <>
      {/* inert while the intro plays, so focus and taps stay on the film. */}
      <main className="screen screen--center" inert={intro}>
        <h1 className="brand brand--login">
          <PointzLogo animate />
        </h1>
        <div className="segmented" role="tablist" aria-label="Who is signing in">
          <button
            type="button"
            role="tab"
            aria-selected={who === "adult"}
            className="segmented__option"
            onClick={() => setWho("adult")}
          >
            Grown-up
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={who === "kid"}
            className="segmented__option"
            onClick={() => setWho("kid")}
          >
            Kid
          </button>
        </div>
        {who === "adult" ? <AdultSignIn /> : <KidSignIn />}
        <WatchIntro onOpen={() => setIntro(true)} />
        {showPreview && <PreviewSignIn />}
      </main>
      {intro && <IntroPlayer onClose={closeIntro} signedIn={false} />}
    </>
  );
}

/**
 * A quiet way to see what Pointz is before signing in. A device that has never had the intro
 * also gets the poster as a thumbnail, so a first visit can see there is a film to watch.
 */
function WatchIntro({ onOpen }: { onOpen: () => void }) {
  const [firstVisit] = useState(shouldShowIntro);
  return (
    <button className="button button--ghost watch-intro" type="button" onClick={onOpen}>
      {firstVisit && <img className="watch-intro__poster" src={INTRO_POSTER} alt="" width={36} height={64} />}
      <span>
        <span aria-hidden="true">{"▶︎"}</span> Watch the intro
      </span>
    </button>
  );
}

function AdultSignIn() {
  const { requestCode, verifyCode } = useStore();
  // Read once into this form's state, then forgotten, so it prefills only the first form shown.
  const [invited] = useState(invitedEmail);
  const [email, setEmail] = useState(invited);
  useEffect(forgetInvite, []);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await requestCode(email.trim());
      setSent(true);
    } catch (e2) {
      setErr(errorText(e2, "Something went wrong. Try again."));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await verifyCode(email.trim(), code);
    } catch (e2) {
      setErr(errorText(e2, "That code didn't work. Check it, or send a new one."));
      setBusy(false);
    }
  }

  if (!sent) {
    return (
      <form className="stack" onSubmit={(e) => void send(e)}>
        {invited && <p className="hint">You're invited. We'll email a 6-digit code to sign you in.</p>}
        <label className="field">
          <span className="field__label">Your email</span>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        {err && <p className="error">{err}</p>}
        <button className="button button--primary" type="submit" disabled={busy || !email.includes("@")}>
          {busy ? "Sending…" : "Email me a code"}
        </button>
      </form>
    );
  }

  return (
    <form className="stack" onSubmit={(e) => void verify(e)}>
      <p className="lead">
        If <strong className="ph-no-capture">{email}</strong> belongs to this family, a 6-digit code is on its way.
      </p>
      <label className="field">
        <span className="field__label">Code</span>
        <input
          className="input input--code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="123456"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </label>
      {err && <p className="error">{err}</p>}
      <button className="button button--primary" type="submit" disabled={busy || code.length !== 6}>
        {busy ? "Checking…" : "Sign in"}
      </button>
      <button className="button button--ghost" type="button" onClick={() => void send()} disabled={busy}>
        Send a new code
      </button>
    </form>
  );
}

function KidSignIn() {
  const { kidSignIn } = useStore();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await kidSignIn(code);
    } catch (e2) {
      // A wrong code, a locked phone and a busy hour all answer 401; the words stay kind for each.
      const status = (e2 as { status?: number }).status;
      setErr(status === 401 ? KID_FAILED : errorText(e2, KID_FAILED));
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <label className="field">
        <span className="field__label">Your code</span>
        <input
          className="input input--code"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={KID_CODE_LENGTH + 2}
          placeholder="ABC234"
          autoFocus
          value={code}
          onChange={(e) => setCode(normaliseKidCode(e.target.value))}
        />
      </label>
      {err && <p className="error">{err}</p>}
      <button
        className="button button--primary"
        type="submit"
        disabled={busy || code.length !== KID_CODE_LENGTH}
      >
        {busy ? "Checking…" : "Let me in"}
      </button>
      <p className="hint">Ask a grown-up for your code.</p>
    </form>
  );
}

function PreviewSignIn() {
  const { previewSignIn } = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await previewSignIn();
    } catch (e) {
      setErr(errorText(e, "Preview sign-in failed. Is this a preview deploy?"));
      setBusy(false);
    }
  }

  return (
    <div className="stack preview">
      <button className="button button--ghost" type="button" onClick={() => void go()} disabled={busy}>
        {busy ? "Signing in…" : "Preview sign-in"}
      </button>
      <p className="hint">Preview build: one tap signs you in as the grown-up. Not shown in production.</p>
      {err && <p className="error">{err}</p>}
    </div>
  );
}
